import { cloneElement, forwardRef, isValidElement, useRef } from 'react';
import { View } from 'react-native';
import { Pressable } from 'react-native';

import composeRefs from '@/utils/composeRefs';

import { useTooltipContext } from './TooltipContext';
import type { TooltipTriggerProps } from './types';

const TooltipTrigger = forwardRef<View, TooltipTriggerProps>(
  ({ children, asChild = false, onPress, ...props }, ref) => {
    const { showTooltip, setTriggerLayout } = useTooltipContext();

    const triggerRef = useRef<View>(null);

    const handlePress = (event: Parameters<NonNullable<typeof onPress>>[0]) => {
      onPress?.(event);

      triggerRef.current?.measureInWindow((pageX, pageY, width, height) => {
        setTriggerLayout({
          x: pageX,
          y: pageY,
          width,
          height,
        });
        showTooltip();
      });
    };

    if (asChild && isValidElement(children)) {
      return (
        <View ref={composeRefs(ref, triggerRef)}>
          {cloneElement(children, { ...props, onPress: handlePress })}
        </View>
      );
    }

    return (
      <Pressable
        onPress={handlePress}
        ref={composeRefs(ref, triggerRef)}
        {...props}
      >
        {children}
      </Pressable>
    );
  },
);

TooltipTrigger.displayName = 'TooltipTrigger';

export { TooltipTrigger };
